import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, RouteReuseStrategy, withComponentInputBinding } from '@angular/router';
import { provideCapturumApi } from '@capturum/api';
import { AuthService, provideCapturumAuth, RefreshTokenData } from '@capturum/auth';
import { provideCapturumComplete } from '@capturum/complete';
import { provideCapturumFormly } from '@capturum/formly';
import { provideCapturumLogin } from '@capturum/login';
import { CapturumMissingTranslationHandler, provideCapturumShared } from '@capturum/shared';
import { provideCapturumDynamicFilters, provideCapturumUI } from '@capturum/ui/config';
import {
  DEFAULT_LANGUAGE,
  FEEDBACK_WIDGET_SELECTOR,
  KEY_MODIFIER,
  SESSION_STORAGE,
  USER_PLATFORM,
  WINDOW,
} from '@core/configs/token.config';
import { DateTimeRangeFilterComponent } from '@core/dynamic-filters/components/date-time-range-filter/date-time-range-filter.component';
import { CustomDynamicFilterType } from '@core/dynamic-filters/enums/custom-dynamic-filter-type';
import { SentryErrorHandler } from '@core/error-handlers/SentryErrorHandler';
import { formlyTypes, formlyValidators, formlyWrappers } from '@core/formly-components/formly-types';
import { detectOS, modifierKeys } from '@core/helpers/key-event.helper';
import { KeyModifier } from '@core/interfaces/key-modifier.interface';
import { CustomRouteReuseStrategy } from '@core/providers/custom-route-reuse-strategy.provider';
import { environment } from '@environments/environment';
import { FormlyModule } from '@ngx-formly/core';
import {
  MissingTranslationHandler,
  provideTranslateService,
  TranslateCompiler,
  TranslateLoader,
  TranslateService,
} from '@ngx-translate/core';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { provideScReCaptchaSettings } from '@semantic-components/re-captcha';
import * as Sentry from '@sentry/angular';
import { errorMessageInterceptor } from '@shared/interceptors/error-message.interceptor';
import { httpCodeInterceptor } from '@shared/interceptors/http-code.interceptor';
import { notAuthorizedInterceptor } from '@shared/interceptors/not-authorized.interceptor';
import { tenantRefInterceptor } from '@shared/interceptors/tenant-ref.interceptor';
import { NeqtoAuthService } from '@shared/services/neqto-auth.service';
import { TranslateServiceWrapper } from '@shared/services/translate-service-wrapper.service';
import 'hammerjs';
import { en_US, provideNzI18n } from 'ng-zorro-antd/i18n';
import { DndModule } from 'ngx-drag-drop';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideQuillConfig } from 'ngx-quill';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { ConfirmationService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { PopoverModule } from 'primeng/popover';
import { Observable } from 'rxjs';
import { neqtoPreset } from './app-preset';
import { appRoutes } from './app-routing.module';

// Stub onAuthError function, since we don't redirect to login for status 401
// it should be handled in the interceptor
function onAuthError(): void {}

function windowFactory(): Window {
  return window;
}

function platformFactory(): string {
  const window = inject(WINDOW);

  return detectOS(window);
}

function keyModifierFactory(): KeyModifier {
  const platform = inject(USER_PLATFORM);

  return modifierKeys(platform);
}

class CustomLoader implements TranslateLoader {
  http = inject(HttpClient);

  getTranslation(lang: string): Observable<any> {
    return this.http.get(`./assets/i18n/${lang}.json?v=${new Date().getTime()}`);
  }
}

function checkJWTToken(authService: NeqtoAuthService): () => Observable<boolean | RefreshTokenData> {
  return () => {
    return authService.checkJwtToken();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    DialogService,
    provideRouter(appRoutes, withComponentInputBinding()),
    provideCapturumLogin({
      backgroundImageUrl: '/assets/images/login-background.jpg',
      logoImageUrl: '/assets/images/eneqto-infinity-light.svg',
      productName: 'ENEQTO',
    }),
    provideStore([]),
    provideCapturumShared(),
    provideCapturumAuth({
      unauthenticatedRejectionRoute: '',
      baseUrl: environment.baseUrl,
      production: environment.production,
      authRejectionRoute: 'auth/login',
      authRefreshToken: {
        useRefreshToken: true,
        offsetSeconds: 60,
        intervalSeconds: 3,
      },
    }),
    provideCapturumApi({
      baseUrl: environment.baseUrl,
      production: environment.production,
      onAuthError,
    }),
    provideCapturumComplete({
      indexedDbModels: [],
      databaseName: 'mytest-db',
      version: 1,
      environment,
    }),
    provideCapturumFormly(),
    provideCapturumUI({ primaryColor: '#10b981' }),
    providePrimeNG({
      theme: {
        preset: neqtoPreset,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    provideCapturumDynamicFilters({
      components: [
        {
          name: CustomDynamicFilterType.dateTimeRange,
          component: DateTimeRangeFilterComponent,
        },
      ],
    }),
    importProvidersFrom(
      BrowserModule,
      ConfirmDialogModule,
      DynamicDialogModule,
      DndModule,
      PopoverModule,
      NgxPermissionsModule.forRoot(),
      FormlyModule.forRoot({
        validators: [...formlyValidators],
        wrappers: [...formlyWrappers],
        validationMessages: [
          { name: 'url', message: 'Invalid URL' },
          { name: 'version', message: 'Invalid version' },
          { name: 'dtoKey', message: 'Wrong format, closing brackets are missing' },
        ],
        types: [...formlyTypes],
      }),
    ),
    provideScReCaptchaSettings({
      v3SiteKey: environment.recaptchaKey,
    }),
    provideHttpClient(
      withInterceptors([tenantRefInterceptor, httpCodeInterceptor, errorMessageInterceptor, notAuthorizedInterceptor]),
    ),
    provideNzI18n(en_US),
    ConfirmationService,
    withNgxsReduxDevtoolsPlugin({ disabled: environment.production }),
    provideAnimations(),
    provideTranslateService({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CapturumMissingTranslationHandler,
      },
      loader: {
        provide: TranslateLoader,
        useClass: CustomLoader,
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
    }),
    provideQuillConfig({
      modules: {
        syntax: false,
        toolbar: {
          container: [
            ['bold', 'italic', 'underline', 'strike'], // toggled buttons
            ['blockquote', 'code-block'],

            [{ header: 1 }, { header: 2 }], // custom button values
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
            [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
            [{ direction: 'rtl' }], // text direction

            [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
            [{ header: [1, 2, 3, 4, 5, 6, false] }],

            [{ color: [] }, { background: [] }], // dropdown with defaults from theme
            [{ font: [] }],
            [{ align: [] }],

            ['clean'], // remove formatting button

            ['link', 'image', 'video'], // link and image, video
          ],
        },
      },
    }),
    {
      provide: AuthService,
      useClass: NeqtoAuthService,
    },
    {
      provide: TranslateService,
      useClass: TranslateServiceWrapper,
    },
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    provideAppInitializer(() => {
      const authService = inject(NeqtoAuthService);

      return authService.checkJwtToken();
    }),
    {
      provide: DEFAULT_LANGUAGE,
      useValue: 'en',
    },
    {
      provide: WINDOW,
      useFactory: windowFactory,
    },
    {
      provide: SESSION_STORAGE,
      useFactory: () => {
        const window = inject(WINDOW);

        return window.sessionStorage;
      },
    },
    {
      provide: USER_PLATFORM,
      useFactory: platformFactory,
    },
    {
      provide: KEY_MODIFIER,
      useFactory: keyModifierFactory,
    },
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      const traceService = inject(Sentry.TraceService);
    }),
    {
      provide: FEEDBACK_WIDGET_SELECTOR,
      useValue: '#feedbackWidget',
    },
  ],
};
